
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { ProfileHeader } from "@/components/profile/ProfileHeader";
import { ProfileField } from "@/components/profile/ProfileField";
import { ProfileActions } from "@/components/profile/ProfileActions";
import { ProfileAvatar } from "@/components/profile/ProfileAvatar";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

const Profile = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [editedEmail, setEditedEmail] = useState("");
  const [editedPhone, setEditedPhone] = useState("");
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch current auth user
  const { data: authUser } = useQuery({
    queryKey: ['auth-user'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
  });

  // Fetch user profile data
  const { data: userData, isLoading: isLoadingProfile } = useQuery({
    queryKey: ['user-profile', authUser?.id],
    enabled: !!authUser?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('user_id', authUser?.id)
        .maybeSingle();

      if (error) {
        toast({
          variant: "destructive",
          title: "Error fetching profile",
          description: error.message,
        });
        throw error;
      }

      return data;
    },
  });

  // Set initial form values when data is loaded
  useEffect(() => {
    if (userData) {
      setEditedName(userData.full_name || '');
      setEditedEmail(userData.email || '');
      setEditedPhone(userData.phone_number || '');
    }
  }, [userData]);

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: async (profileData: {
      full_name: string;
      email: string;
      phone_number: string | null;
      avatar_url?: string;
    }) => {
      if (!authUser?.id) throw new Error('User is not authenticated');
      
      const { error } = await supabase
        .from('users')
        .update(profileData)
        .eq('user_id', authUser.id);

      if (error) throw error;
    },
    onSuccess: () => {
      setIsEditing(false);
      queryClient.invalidateQueries({ queryKey: ['user-profile', authUser?.id] });
      toast({
        title: "Profile updated",
        description: "Your profile has been successfully updated.",
      });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Error updating profile",
        description: error instanceof Error ? error.message : 'An error occurred',
      });
    }
  });

  const handleSave = () => {
    updateProfileMutation.mutate({
      full_name: editedName,
      email: editedEmail,
      phone_number: editedPhone || null,
    });
  };

  const handleAvatarChange = (avatarUrl: string) => {
    if (!authUser?.id || !userData) return;
    
    updateProfileMutation.mutate({
      full_name: userData.full_name || '',
      email: userData.email || '',
      phone_number: userData.phone_number || null,
      avatar_url: avatarUrl,
    });
  };

  const handleSignOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast({
        variant: "destructive",
        title: "Error signing out",
        description: error.message,
      });
    } else {
      navigate('/login');
    }
  };

  if (isLoadingProfile) {
    return (
      <div className="container max-w-2xl mx-auto p-4">
        <Card>
          <CardContent className="py-8">
            <div className="flex items-center justify-center">
              <p className="text-muted-foreground">Loading profile...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!authUser || !userData) {
    return (
      <div className="container max-w-2xl mx-auto p-4">
        <Card>
          <CardContent className="py-8">
            <div className="flex items-center justify-center">
              <p className="text-muted-foreground">Profile not found</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container max-w-2xl mx-auto p-4 pb-24">
      <ProfileHeader avatarUrl={userData.avatar_url} />

      <Card>
        <CardHeader className="pb-0">
          <ProfileAvatar 
            userId={authUser.id}
            avatarUrl={userData.avatar_url}
            firstName={userData.full_name}
            lastName=""
            onAvatarChange={handleAvatarChange}
          />
        </CardHeader>
        <CardContent className="space-y-4 pt-6">
          <ProfileField
            label="Name"
            id="name"
            value={editedName}
            onChange={setEditedName}
            isEditing={isEditing}
          />
          <ProfileField
            label="Email"
            id="email"
            type="email"
            value={editedEmail}
            onChange={setEditedEmail}
            isEditing={isEditing}
          />
          <ProfileField
            label="Phone"
            id="phone"
            type="tel"
            value={editedPhone}
            onChange={setEditedPhone}
            isEditing={isEditing}
            placeholder="+1234567890"
          />
          
          <ProfileActions
            isEditing={isEditing}
            onSave={handleSave}
            onEdit={() => setIsEditing(true)}
            onSignOut={handleSignOut}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default Profile;
